import React, { useEffect, useState } from 'react';
import api, { unwrap } from '../utils/api';
import Icon from './Icon';

const DISMISS_KEY = 'itsasimcha_announcement_dismissed_v';

// Small dismissable popup shown when the admin has the announcement toggle on.
// Slides down from the top and stays pinned there. Dismissal is per-session
// and per-message-version, so editing the message in admin re-shows it to
// visitors who had closed the previous one.
export default function AnnouncementPopup() {
  const [state, setState] = useState({ loaded: false, enabled: false, message: '', version: 0 });
  const [dismissed, setDismissed] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchAnn() {
      try {
        const r = await api.get('/miscellaneous/announcement');
        const v = unwrap(r) || { enabled: false, message: '', version: 0 };
        if (cancelled) return;
        setState({
          loaded: true,
          enabled: !!v.enabled,
          message: v.message || '',
          version: Number(v.version) || 0,
        });
      } catch (_) {
        if (cancelled) return;
        setState({ loaded: true, enabled: false, message: '', version: 0 });
      }
    }
    fetchAnn();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!state.loaded) return;
    try {
      const v = sessionStorage.getItem(DISMISS_KEY);
      setDismissed(v && Number(v) === state.version);
    } catch (_) {
      setDismissed(false);
    }
  }, [state.loaded, state.version]);

  const visible = state.loaded && state.enabled && state.message.trim() && !dismissed;

  useEffect(() => {
    if (!visible) { setShown(false); return undefined; }
    const t = setTimeout(() => setShown(true), 50);
    return () => clearTimeout(t);
  }, [visible]);

  function close() {
    try { sessionStorage.setItem(DISMISS_KEY, String(state.version)); } catch (_) {}
    setDismissed(true);
  }

  if (!visible) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 20,
        left: '50%',
        transform: `translateX(-50%) translateY(${shown ? '0' : '-150%'})`,
        maxWidth: 480,
        width: 'calc(100% - 32px)',
        background: '#e6f4fc',
        color: '#0a3d5c',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(10, 95, 180, 0.18)',
        padding: '16px 44px 16px 20px',
        fontFamily: 'inherit',
        fontSize: 15,
        lineHeight: 1.5,
        zIndex: 9999,
        border: '2px solid #29B4F1',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {state.message}
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          width: 28,
          height: 28,
          border: 'none',
          background: 'transparent',
          color: '#0a5fb4',
          fontSize: 22,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 0,
          borderRadius: 4,
        }}
      >
        <Icon name="close" size="22px" />
      </button>
    </div>
  );
}
